import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import { useCallback, useContext, type ReactNode } from 'react'
import { SnackbarOptions } from './Snackbar'
import { SnackbarContext } from './SnackbarContext'

export const useSnackbar = () => {
  const context = useContext(SnackbarContext)

  if (context === undefined) {
    throw new Error('useSnackbar must be used within a SnackbarProvider')
  }

  const { showMessage, hideMessage } = context

  const showSnackbar = useCallback(
    (message: ReactNode, options?: SnackbarOptions) => {
      showMessage(message, options)
    },
    [showMessage],
  )

  const hideSnackbar = useCallback(() => {
    hideMessage()
  }, [hideMessage])

  const showErrorSnackbar = useCallback(
    (message: ReactNode, options?: SnackbarOptions) => {
      showSnackbar(message, {
        anchorOrigin: { vertical: 'top', horizontal: 'center' },
        severity: 'error',
        variant: 'standard',
        autoHideDuration: 6000,
        ...options,
      })
    },
    [showSnackbar],
  )

  const showSuccessSnackbar = useCallback(
    (message: ReactNode, options?: SnackbarOptions) => {
      showSnackbar(message, {
        anchorOrigin: { vertical: 'top', horizontal: 'center' },
        severity: 'success',
        variant: 'standard',
        icon: <CheckCircleOutlineIcon />,
        autoHideDuration: 6000,
        ...options,
      })
    },
    [showSnackbar],
  )

  const showInfoSnackbar = useCallback(
    (message: ReactNode, options?: SnackbarOptions) => {
      showSnackbar(message, {
        anchorOrigin: { vertical: 'top', horizontal: 'center' },
        severity: 'info',
        variant: 'standard',
        icon: <InfoOutlinedIcon />,
        autoHideDuration: 6000,
        ...options,
      })
    },
    [showSnackbar],
  )

  const showWarningSnackbar = useCallback(
    (message: ReactNode, options?: SnackbarOptions) => {
      showSnackbar(message, {
        anchorOrigin: { vertical: 'top', horizontal: 'center' },
        severity: 'warning',
        variant: 'standard',
        autoHideDuration: 6000,
        ...options,
      })
    },
    [showSnackbar],
  )

  return {
    showSnackbar,
    showErrorSnackbar,
    showSuccessSnackbar,
    showInfoSnackbar,
    showWarningSnackbar,
    hideSnackbar,
  } as const
}
